import { useState } from "react";
import {
    LuCirclePause,
    LuCirclePlay,
    LuDownload,
    LuFastForward,
    LuTrash2,
} from "react-icons/lu";
import ConfirmationDialog from "../../../../shared/ConfirmationDialog";
import { QueueFilter, SendType } from "../constants";
import { useBulkNotificationActions } from "./useBulkNotificationActions";
import "./BottomBar.scss";

const ICON_SIZE = 18;

/**
 * Queue footer: bulk actions when the Pending tab is selected; Download CSV always; total row count.
 * @param {object} props
 * @param {import("../useNotifications").UseNotificationsProps} props.useNotificationsHookData Full {@link useNotifications} bag;
 *   this footer reads `sendType`, `queueFilter`, `columnFilters`, `notificationsTotalCount`, `isNotificationsTotalCountError`, and `isLoading` (as `actionsDisabled` for buttons);
 *   confirm `onConfirm` handlers can use the full bag for current filters / pagination.
 * @returns {import('react').JSX.Element}
 */
const BottomBar = ({ useNotificationsHookData }) => {
    const {
        sendType,
        queueFilter,
        columnFilters,
        notifications,
        notificationsTotalCount,
        isNotificationsTotalCountLoading,
        isNotificationsTotalCountError,
        isLoading: actionsDisabled,
        refreshNotificationsList,
    } = useNotificationsHookData;

    const {
        downloadCsvMutation,
        bulkMoveMutation,
        bulkSendMutation,
        bulkDeleteMutation,
    } = useBulkNotificationActions({
        sendType,
        queueFilter,
        columnFilters,
        refreshNotificationsList,
    });

    const isAutomated = sendType === SendType.AUTOMATED;
    const isPendingFilter = queueFilter === QueueFilter.PENDING;

    const loadedRowCount = notifications.length;
    const hasServerTotal = notificationsTotalCount !== undefined;
    const isEmptyForActions = isNotificationsTotalCountError
        ? loadedRowCount === 0
        : hasServerTotal
          ? notificationsTotalCount === 0
          : loadedRowCount === 0;

    const disableActions = actionsDisabled || isEmptyForActions;

    const totalCountDisplay =
        notificationsTotalCount === undefined
            ? isNotificationsTotalCountLoading
                ? "…"
                : "—"
            : String(notificationsTotalCount);

    const [confirmation, setConfirmation] = useState(null);

    const disableDownloadCsv = disableActions || downloadCsvMutation.isPending;

    const handleConfirmationOpenChange = (isOpen) => {
        if (!isOpen) {
            setConfirmation(null);
        }
    };

    const downloadCsvButton = (
        <button
            type="button"
            className="notifications-queue__footer-action"
            disabled={disableDownloadCsv}
            aria-busy={downloadCsvMutation.isPending}
            onClick={() => downloadCsvMutation.mutate()}
        >
            {downloadCsvMutation.isPending ? (
                <span
                    className="loading-with-label"
                    role="status"
                    aria-live="polite"
                >
                    Downloading CSV
                    <span
                        className="loader loader--inline"
                        aria-hidden="true"
                    />
                </span>
            ) : (
                <>
                    Download CSV
                    <LuDownload aria-hidden size={ICON_SIZE} />
                </>
            )}
        </button>
    );

    return (
        <>
            <ConfirmationDialog
                open={confirmation !== null}
                onOpenChange={handleConfirmationOpenChange}
                title={confirmation?.title}
                body={confirmation?.body}
                confirmLabel={confirmation?.confirmLabel}
                confirmPendingLabel={confirmation?.confirmPendingLabel}
                onConfirm={confirmation?.onConfirm}
            />

            <div className="notifications-queue__bottom-bar">
                <div className="notifications-queue__footer-actions">
                    {isPendingFilter && (
                        <>
                            <button
                                type="button"
                                className="notifications-queue__footer-action"
                                disabled={disableActions}
                                onClick={() =>
                                    setConfirmation({
                                        title: isAutomated
                                            ? "Pause all filtered notifications?"
                                            : "Automate all filtered notifications?",
                                        body: isAutomated
                                            ? "Pausing will move ALL notifications based on the current filters to the Manual tab."
                                            : "Automating will resume sending ALL notifications based on the current filters and move them to the Automated tab.",
                                        confirmLabel: isAutomated
                                            ? "Pause filtered notifications"
                                            : "Resume sending filtered notifications",
                                        confirmPendingLabel: isAutomated
                                            ? "Pausing notifications"
                                            : "Resuming notifications",
                                        onConfirm: () =>
                                            bulkMoveMutation.mutateAsync({
                                                targetSendType: isAutomated
                                                    ? SendType.MANUAL
                                                    : SendType.AUTOMATED,
                                            }),
                                    })
                                }
                            >
                                {isAutomated ? (
                                    <>
                                        Move to Manual
                                        <LuCirclePause
                                            aria-hidden
                                            size={ICON_SIZE}
                                        />
                                    </>
                                ) : (
                                    <>
                                        Move to Automated
                                        <LuFastForward
                                            aria-hidden
                                            size={ICON_SIZE}
                                        />
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                className="notifications-queue__footer-action"
                                disabled={disableActions}
                                onClick={() =>
                                    setConfirmation({
                                        title: "Send all filtered notifications now?",
                                        body: "This will send ALL notifications based on the current filters immediately.",
                                        confirmLabel:
                                            "Immediately send all filtered notifications",
                                        confirmPendingLabel:
                                            "Sending notifications",
                                        onConfirm: () =>
                                            bulkSendMutation.mutateAsync(),
                                    })
                                }
                            >
                                Send Now
                                <LuCirclePlay aria-hidden size={ICON_SIZE} />
                            </button>
                            <button
                                type="button"
                                className="notifications-queue__footer-action"
                                disabled={disableActions}
                                onClick={() =>
                                    setConfirmation({
                                        title: "Delete all filtered notifications?",
                                        body: "This will permanently delete ALL notifications based on the current filters. This action cannot be undone.",
                                        confirmLabel:
                                            "Delete all filtered notifications",
                                        confirmPendingLabel:
                                            "Deleting notifications",
                                        onConfirm: () =>
                                            bulkDeleteMutation.mutateAsync(),
                                    })
                                }
                            >
                                Delete
                                <LuTrash2 aria-hidden size={ICON_SIZE} />
                            </button>
                        </>
                    )}
                    {downloadCsvButton}
                </div>

                {isNotificationsTotalCountError ? (
                    <p
                        className="notifications-queue__count notifications-queue__count--error"
                        role="alert"
                    >
                        Error loading total count
                    </p>
                ) : (
                    <p
                        className="notifications-queue__count"
                        aria-live="polite"
                    >
                        Total count: {totalCountDisplay}
                    </p>
                )}
            </div>
        </>
    );
};

export default BottomBar;
